'use client';

import { getCmsLocalizedHref } from '@/cms/chrome/localized-href';
import type { CmsShellViewerMode } from '@/cms/chrome/shell-viewer-mode';
import type { CmsWorkspaceSidebarActionAppearance } from '@/cms/chrome/workspace-sidebar-action-appearance';
import { CmsEditorialIcon } from '@/cms/components/blocks/shared/cms-editorial-icon';
import { WorkspaceMobileDrawer } from '@/cms/components/blocks/workspace/workspace-1/shell/workspace-mobile-drawer';
import { useWorkspaceViewportKind } from '@/cms/components/blocks/workspace/workspace-1/shell/workspace-viewport';
import { useCmsAccountLocalCopy } from '@/cms/components/chrome/cms-account-menu';
import { CmsNavLinkBadge } from '@/cms/components/chrome/cms-nav-link-badge';
import { CmsWorkspaceSidebarAuthPanel } from '@/cms/components/chrome/cms-workspace-sidebar-auth-panel';
import { renderAppIcon } from '@/components/icons/app-icon-registry';
import { Logo } from '@/components/layout/system/logo';
import { useAuthSessionObserver } from '@/lib/auth/auth-session-observer';
import { getCurrentBrowserPathWithSearch } from '@/lib/browser/current-location';
import { stripLocalePrefix } from '@/lib/i18n/locale-path';
import { cn } from '@/lib/ui/utils';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import type { ReactNode } from 'react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CmsWorkspaceSidebarTools } from './cms-workspace-sidebar-tools';
import type { CmsWorkspaceSidebarUtilityItem } from './cms-workspace-sidebar-utility-item';
import styles from './cms-workspace-sidebar.module.css';

export const CMS_WORKSPACE_SIDEBAR_DEFAULT_EXPANDED = true;

export type CmsWorkspaceSidebarDestination =
  | { type: 'href'; href: string }
  | { type: 'action'; actionKey: string };

export type CmsWorkspaceSidebarChildItem = {
  label: string;
  destination: CmsWorkspaceSidebarDestination;
  toolId?: string;
  icon?: string;
  badgeLabel?: string;
};

export type CmsWorkspaceSidebarChildGroup = {
  title?: string;
  links: CmsWorkspaceSidebarChildItem[];
};

export type CmsWorkspaceSidebarItem = Omit<
  CmsWorkspaceSidebarChildItem,
  'icon'
> & {
  id: string;
  appearance?: CmsWorkspaceSidebarActionAppearance;
  icon?: string;
  children: CmsWorkspaceSidebarChildItem[];
  childGroups: CmsWorkspaceSidebarChildGroup[];
};

type CmsWorkspaceSidebarEntry = {
  key: string;
  item: CmsWorkspaceSidebarItem;
};

export type CmsWorkspaceSidebarUtilityCopy = {
  moreLabel: string;
  emailLabel: string;
  discordLabel: string;
  languageLabel: string;
  expandSidebarLabel: string;
  collapseSidebarLabel: string;
};

export type CmsWorkspaceSidebarModel = {
  brandLabel: string;
  brandDestination: CmsWorkspaceSidebarDestination;
  utilityCopy: CmsWorkspaceSidebarUtilityCopy;
  navItems: CmsWorkspaceSidebarItem[];
  bottomNavItems: CmsWorkspaceSidebarItem[];
  utilityItems: CmsWorkspaceSidebarUtilityItem[];
};

function isExternalHref(href: string) {
  return (
    href.startsWith('http://') ||
    href.startsWith('https://') ||
    href.startsWith('mailto:') ||
    href.startsWith('tel:') ||
    href.startsWith('#')
  );
}

function normalizeSidebarActivePath(value: string) {
  const trimmed = value.trim();
  if (!trimmed || isExternalHref(trimmed) || trimmed.includes('#')) {
    return null;
  }

  const basePath = trimmed.split(/[?#]/)[0] ?? '/';
  const normalized = stripLocalePrefix(basePath || '/');
  if (normalized === '/') {
    return '/';
  }

  return normalized.endsWith('/') ? normalized.slice(0, -1) : normalized;
}

function isSidebarHrefActive(currentPath: string, href: string) {
  const normalizedCurrentPath = normalizeSidebarActivePath(currentPath);
  const normalizedHref = normalizeSidebarActivePath(href);

  if (!normalizedCurrentPath || !normalizedHref) {
    return false;
  }

  if (normalizedHref === '/') {
    return normalizedCurrentPath === '/';
  }

  return (
    normalizedCurrentPath === normalizedHref ||
    normalizedCurrentPath.startsWith(`${normalizedHref}/`)
  );
}

function isSidebarChildItemActive(
  child: CmsWorkspaceSidebarChildItem,
  currentPath: string
) {
  if (child.destination.type !== 'href') {
    return false;
  }

  return isSidebarHrefActive(currentPath, child.destination.href);
}

function isSidebarItemActive(
  item: CmsWorkspaceSidebarItem,
  currentPath: string
) {
  if (
    item.destination.type === 'href' &&
    isSidebarHrefActive(currentPath, item.destination.href)
  ) {
    return true;
  }

  return (
    item.children.some((child) =>
      isSidebarChildItemActive(child, currentPath)
    ) ||
    item.childGroups.some((group) =>
      group.links.some((child) => isSidebarChildItemActive(child, currentPath))
    )
  );
}

function SidebarBrandLabel({ label }: { label: string }) {
  const suffixStart = label.lastIndexOf('.');

  if (suffixStart <= 0 || suffixStart === label.length - 1) {
    return <span className={styles.brandLabel}>{label}</span>;
  }

  return (
    <span className={styles.brandLabel}>
      <span>{label.slice(0, suffixStart)}</span>
      <span className={styles.brandLabelSuffix}>
        {label.slice(suffixStart)}
      </span>
    </span>
  );
}

function SidebarDestinationTrigger(props: {
  locale: string;
  destination: CmsWorkspaceSidebarDestination;
  className: string;
  children: ReactNode;
  ariaLabel?: string;
  current?: 'page' | undefined;
  onAction: (actionKey: string) => void;
  onMouseEnter?: () => void;
  onClick?: () => void;
  role?: string;
}) {
  if (props.destination.type === 'action') {
    const { actionKey } = props.destination;
    return (
      <button
        type="button"
        aria-label={props.ariaLabel}
        className={props.className}
        data-active={props.current ? 'true' : undefined}
        onMouseEnter={props.onMouseEnter}
        onClick={() => {
          props.onAction(actionKey);
          props.onClick?.();
        }}
        role={props.role}
      >
        {props.children}
      </button>
    );
  }

  if (isExternalHref(props.destination.href)) {
    return (
      <Link
        href={props.destination.href}
        aria-label={props.ariaLabel}
        aria-current={props.current}
        className={props.className}
        data-active={props.current ? 'true' : undefined}
        onMouseEnter={props.onMouseEnter}
        onClick={props.onClick}
        role={props.role}
      >
        {props.children}
      </Link>
    );
  }

  return (
    <Link
      href={getCmsLocalizedHref(props.locale, props.destination.href)}
      aria-label={props.ariaLabel}
      aria-current={props.current}
      className={props.className}
      data-active={props.current ? 'true' : undefined}
      onMouseEnter={props.onMouseEnter}
      onClick={props.onClick}
      role={props.role}
    >
      {props.children}
    </Link>
  );
}

function SidebarItemLabel({
  item,
  labelOverride,
  surface = 'sidebar',
}: {
  item: CmsWorkspaceSidebarItem;
  labelOverride?: string;
  surface?: 'account-menu' | 'sidebar';
}) {
  return (
    <span
      className={
        surface === 'account-menu'
          ? styles.accountMenuPricingActionText
          : styles.navText
      }
    >
      <span>{labelOverride ?? item.label}</span>
      {item.badgeLabel ? <CmsNavLinkBadge label={item.badgeLabel} /> : null}
    </span>
  );
}

function SidebarDirectLink({
  appearance = 'default',
  item,
  locale,
  muted = false,
  isActive,
  onAction,
  onHover,
  labelOverride,
  surface = 'sidebar',
}: {
  appearance?: CmsWorkspaceSidebarActionAppearance;
  item: CmsWorkspaceSidebarItem;
  locale: string;
  muted?: boolean;
  isActive: boolean;
  onAction: (actionKey: string) => void;
  onHover?: () => void;
  labelOverride?: string;
  surface?: 'account-menu' | 'sidebar';
}) {
  const isAccountMenuAction = surface === 'account-menu';
  const iconElement =
    !isAccountMenuAction && item.icon?.trim() ? (
      <CmsEditorialIcon className={styles.navIcon} name={item.icon} />
    ) : null;

  const trigger = (
    <SidebarDestinationTrigger
      locale={locale}
      destination={item.destination}
      ariaLabel={labelOverride ?? item.label}
      className={cn(
        isAccountMenuAction ? styles.accountMenuPricingAction : styles.navLink,
        appearance === 'pricing-action' &&
          !isAccountMenuAction &&
          styles.pricingAction,
        muted && styles.navLinkMuted,
        isActive && styles.navLinkActive
      )}
      current={isActive ? 'page' : undefined}
      onAction={onAction}
      onMouseEnter={onHover}
    >
      {iconElement}
      <SidebarItemLabel
        item={item}
        labelOverride={labelOverride}
        surface={surface}
      />
    </SidebarDestinationTrigger>
  );

  if (appearance === 'pricing-action') {
    return (
      <div
        className={
          isAccountMenuAction
            ? styles.accountMenuPricingActionShell
            : styles.pricingActionShell
        }
        data-sidebar-action="pricing"
        data-sidebar-action-surface={surface}
      >
        {trigger}
      </div>
    );
  }

  return trigger;
}

function SidebarExpandableLink({
  item,
  isOpen,
  onOpen,
  buttonRef,
  isActive,
}: {
  item: CmsWorkspaceSidebarItem;
  isOpen: boolean;
  onOpen: () => void;
  buttonRef: (node: HTMLButtonElement | null) => void;
  isActive: boolean;
}) {
  const iconElement = item.icon?.trim() ? (
    <CmsEditorialIcon className={styles.navIcon} name={item.icon} />
  ) : null;

  return (
    <div className={styles.navItem}>
      <button
        ref={buttonRef}
        type="button"
        className={cn(
          styles.navLink,
          isOpen && styles.navLinkOpen,
          isActive && styles.navLinkActive
        )}
        data-active={isActive ? 'true' : undefined}
        aria-label={item.label}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        onMouseEnter={onOpen}
        onClick={() => {
          onOpen();
        }}
      >
        {iconElement}
        <SidebarItemLabel item={item} />
      </button>
    </div>
  );
}

function SidebarNavItem({
  appearance = 'default',
  item,
  currentPath,
  locale,
  muted = false,
  isOpen = false,
  onAction,
  onOpen,
  onClose,
  buttonRef,
  labelOverride,
  surface = 'sidebar',
}: {
  appearance?: CmsWorkspaceSidebarActionAppearance;
  item: CmsWorkspaceSidebarItem;
  currentPath: string;
  locale: string;
  muted?: boolean;
  isOpen?: boolean;
  onAction: (actionKey: string) => void;
  onOpen: () => void;
  onClose: () => void;
  buttonRef: (node: HTMLButtonElement | null) => void;
  labelOverride?: string;
  surface?: 'account-menu' | 'sidebar';
}) {
  const isActive = isSidebarItemActive(item, currentPath);

  if ((item.children ?? []).length > 0 && !muted) {
    return (
      <SidebarExpandableLink
        item={item}
        isOpen={isOpen}
        onOpen={onOpen}
        buttonRef={buttonRef}
        isActive={isActive}
      />
    );
  }

  return (
    <SidebarDirectLink
      appearance={appearance}
      item={item}
      locale={locale}
      muted={muted}
      isActive={isActive}
      onAction={onAction}
      onHover={onClose}
      labelOverride={labelOverride}
      surface={surface}
    />
  );
}

function SidebarExpandableMenuContent({
  item,
  currentPath,
  locale,
  onAction,
  onNavigate,
  variant = 'desktop',
}: {
  item: CmsWorkspaceSidebarItem;
  currentPath: string;
  locale: string;
  onAction: (actionKey: string) => void;
  onNavigate: () => void;
  variant?: 'desktop' | 'mobile';
}) {
  const isMobile = variant === 'mobile';

  if (isMobile) {
    return (
      <div className={styles.mobileMenuList}>
        {item.childGroups.map((group, groupIndex) => (
          <div
            className={styles.mobileMenuGroup}
            key={`${item.id}-${group.title}-${groupIndex}`}
          >
            {group.title ? (
              <p className={styles.mobileMenuGroupTitle}>{group.title}</p>
            ) : null}
            <div className={styles.menuGroupLinks}>
              {group.links.map((child) => {
                const isActive = isSidebarChildItemActive(child, currentPath);

                return (
                  <SidebarDestinationTrigger
                    locale={locale}
                    key={`${child.label}-${JSON.stringify(child.destination)}`}
                    destination={child.destination}
                    role="menuitem"
                    className={cn(
                      styles.mobileMenuItem,
                      isActive && styles.mobileMenuItemActive
                    )}
                    current={isActive ? 'page' : undefined}
                    onAction={onAction}
                    onClick={onNavigate}
                  >
                    {child.icon?.trim() ? (
                      <CmsEditorialIcon
                        className={styles.mobileMenuItemIcon}
                        name={child.icon}
                      />
                    ) : null}
                    <span className={styles.mobileMenuText}>
                      <span>{child.label}</span>
                      {child.badgeLabel ? (
                        <CmsNavLinkBadge label={child.badgeLabel} />
                      ) : null}
                    </span>
                  </SidebarDestinationTrigger>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.menuGrid}>
      {item.childGroups.map((group, groupIndex) => (
        <div
          key={`${item.id}-${group.title}-${groupIndex}`}
          className={styles.menuGroup}
        >
          {group.title ? (
            <p className={styles.menuGroupTitle}>{group.title}</p>
          ) : null}
          <div className={styles.menuGroupLinks}>
            {group.links.map((child) => {
              const isActive = isSidebarChildItemActive(child, currentPath);

              return (
                <SidebarDestinationTrigger
                  locale={locale}
                  key={`${group.title}-${child.label}-${JSON.stringify(child.destination)}`}
                  destination={child.destination}
                  role="menuitem"
                  className={cn(
                    styles.menuItem,
                    isActive && styles.menuItemActive
                  )}
                  current={isActive ? 'page' : undefined}
                  onAction={onAction}
                  onClick={onNavigate}
                >
                  {child.icon?.trim() ? (
                    <CmsEditorialIcon
                      className={styles.menuItemIcon}
                      name={child.icon}
                    />
                  ) : null}
                  <span className={styles.menuText}>
                    <span>{child.label}</span>
                    {child.badgeLabel ? (
                      <CmsNavLinkBadge
                        className={styles.menuItemBadge}
                        label={child.badgeLabel}
                      />
                    ) : null}
                  </span>
                </SidebarDestinationTrigger>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export function CmsWorkspaceSidebar({
  sidebar,
  currentPath,
  locale,
  onNavigationAction,
  viewerMode = 'live',
  expanded,
  defaultExpanded = CMS_WORKSPACE_SIDEBAR_DEFAULT_EXPANDED,
  onExpandedChange,
}: {
  sidebar: CmsWorkspaceSidebarModel;
  currentPath: string;
  locale: string;
  onNavigationAction?: (actionKey: string) => void;
  viewerMode?: CmsShellViewerMode;
  expanded?: boolean;
  defaultExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}) {
  const accountMenuCopy = useCmsAccountLocalCopy();
  const session = useAuthSessionObserver();
  const historyVisible =
    session.status === 'authenticated' &&
    session.transition?.direction !== 'sign-out';
  const [uncontrolledExpanded, setUncontrolledExpanded] =
    useState(defaultExpanded);
  const [openItemKey, setOpenItemKey] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const viewportKind = useWorkspaceViewportKind();
  const isMobileViewport = viewportKind === 'mobile';
  const pathname = usePathname();
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);
  const resolvedCurrentPath = hasMounted
    ? getCurrentBrowserPathWithSearch(pathname || currentPath)
    : currentPath;
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const topNavEntries = useMemo<CmsWorkspaceSidebarEntry[]>(
    () =>
      sidebar.navItems
        .filter((item) => item.id !== 'workspace-history' || historyVisible)
        .map((item) => ({
          key: item.id,
          item,
        })),
    [sidebar.navItems, historyVisible]
  );
  const bottomNavEntries = useMemo<CmsWorkspaceSidebarEntry[]>(
    () =>
      sidebar.bottomNavItems.map((item) => ({
        key: item.id,
        item,
      })),
    [sidebar.bottomNavItems]
  );
  const subscriptionEntry = bottomNavEntries.find(
    (entry) => entry.item.appearance === 'pricing-action'
  );
  const standardBottomNavEntries = bottomNavEntries.filter(
    (entry) => entry !== subscriptionEntry
  );
  const allNavEntries = useMemo(
    () => [...topNavEntries, ...bottomNavEntries],
    [bottomNavEntries, topNavEntries]
  );
  const expandableEntries = useMemo(
    () => allNavEntries.filter((entry) => entry.item.children.length > 0),
    [allNavEntries]
  );
  const openExpandableEntry =
    allNavEntries.find(
      (entry) => entry.item.children.length > 0 && openItemKey === entry.key
    ) ?? null;
  const openExpandableKey = openExpandableEntry?.key ?? null;
  const isExpanded = !isMobileViewport && (expanded ?? uncontrolledExpanded);
  const executeNavigationAction = useCallback(
    (actionKey: string) => {
      if (!onNavigationAction) {
        throw new Error(
          `Workspace sidebar action ${actionKey} has no action handler.`
        );
      }
      onNavigationAction(actionKey);
    },
    [onNavigationAction]
  );

  const updateMenuPosition = useCallback((key: string) => {
    const node = buttonRefs.current[key];
    if (!node) {
      return;
    }

    const rect = node.getBoundingClientRect();
    setMenuPosition({
      top: rect.top,
      left: rect.right + 12,
    });
  }, []);

  const openMenuFor = useCallback(
    (key: string) => {
      setOpenItemKey(key);
      if (!isMobileViewport) {
        updateMenuPosition(key);
      }
    },
    [isMobileViewport, updateMenuPosition]
  );

  const closeMenu = useCallback(() => {
    setOpenItemKey(null);
  }, []);

  const renderSubscriptionAction = (
    labelOverride?: string,
    surface: 'account-menu' | 'sidebar' = 'sidebar'
  ) =>
    subscriptionEntry ? (
      <SidebarNavItem
        appearance={subscriptionEntry.item.appearance}
        item={subscriptionEntry.item}
        currentPath={resolvedCurrentPath}
        locale={locale}
        isOpen={openItemKey === subscriptionEntry.key}
        onAction={executeNavigationAction}
        onOpen={() => openMenuFor(subscriptionEntry.key)}
        onClose={closeMenu}
        buttonRef={(node) => {
          buttonRefs.current[subscriptionEntry.key] = node;
        }}
        labelOverride={labelOverride}
        surface={surface}
      />
    ) : undefined;

  const setExpanded = useCallback(
    (nextExpanded: boolean) => {
      setUncontrolledExpanded(nextExpanded);
      onExpandedChange?.(nextExpanded);
      closeMenu();
    },
    [closeMenu, onExpandedChange]
  );

  useEffect(() => {
    if (!openItemKey) {
      return;
    }

    const openEntry = allNavEntries.find((entry) => entry.key === openItemKey);
    if (!openEntry?.item.children.length) {
      setOpenItemKey(null);
    }
  }, [allNavEntries, openItemKey]);

  useLayoutEffect(() => {
    if (!openItemKey) {
      return;
    }

    if (isMobileViewport) {
      setMenuPosition(null);
      return;
    }

    updateMenuPosition(openItemKey);

    const handleWindowChange = () => {
      updateMenuPosition(openItemKey);
    };

    window.addEventListener('resize', handleWindowChange);
    window.addEventListener('scroll', handleWindowChange, true);

    return () => {
      window.removeEventListener('resize', handleWindowChange);
      window.removeEventListener('scroll', handleWindowChange, true);
    };
  }, [isMobileViewport, openItemKey, updateMenuPosition]);

  useEffect(() => {
    if (!openItemKey) {
      return;
    }

    if (isMobileViewport) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-cms-workspace-sidebar="root"]')) {
        return;
      }

      closeMenu();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [closeMenu, isMobileViewport, openItemKey]);

  return (
    <aside
      className={cn(styles.root, isExpanded && styles.rootExpanded)}
      data-cms-workspace-sidebar="root"
      data-sidebar-expanded={isExpanded ? 'true' : 'false'}
      data-workspace-viewport={viewportKind}
      onMouseLeave={closeMenu}
    >
      <div className={styles.brandWrap}>
        <SidebarDestinationTrigger
          locale={locale}
          destination={sidebar.brandDestination}
          className={styles.brandLink}
          ariaLabel={sidebar.brandLabel}
          onAction={executeNavigationAction}
        >
          <Logo className={styles.brandLogo} />
          <SidebarBrandLabel label={sidebar.brandLabel} />
        </SidebarDestinationTrigger>
        <button
          type="button"
          className={styles.brandToggle}
          aria-label={
            isExpanded
              ? sidebar.utilityCopy.collapseSidebarLabel
              : sidebar.utilityCopy.expandSidebarLabel
          }
          title={
            isExpanded
              ? sidebar.utilityCopy.collapseSidebarLabel
              : sidebar.utilityCopy.expandSidebarLabel
          }
          onClick={(event) => {
            setExpanded(!isExpanded);
            event.currentTarget.blur();
          }}
        >
          {renderAppIcon({
            name: 'PanelLeft',
            className: cn(
              styles.brandToggleIcon,
              isExpanded && styles.brandToggleIconCollapse
            ),
          })}
        </button>
      </div>

      <nav className={styles.nav}>
        <div className={styles.navGroup}>
          {topNavEntries.map(({ item, key }) => (
            <SidebarNavItem
              key={key}
              item={item}
              currentPath={resolvedCurrentPath}
              locale={locale}
              isOpen={openItemKey === key}
              onAction={executeNavigationAction}
              onOpen={() => openMenuFor(key)}
              onClose={closeMenu}
              buttonRef={(node) => {
                buttonRefs.current[key] = node;
              }}
            />
          ))}
        </div>
      </nav>

      {standardBottomNavEntries.length > 0 ? (
        <div className={styles.bottomNavGroup}>
          {standardBottomNavEntries.map(({ item, key }) => (
            <SidebarNavItem
              appearance={item.appearance}
              key={key}
              item={item}
              currentPath={resolvedCurrentPath}
              locale={locale}
              isOpen={openItemKey === key}
              onAction={executeNavigationAction}
              onOpen={() => openMenuFor(key)}
              onClose={closeMenu}
              buttonRef={(node) => {
                buttonRefs.current[key] = node;
              }}
            />
          ))}
        </div>
      ) : null}

      <div className={styles.utilityGroup}>
        <CmsWorkspaceSidebarAuthPanel
          creditsLabel={accountMenuCopy.creditsLabel}
          expanded={isExpanded}
          locale={locale}
          onNavigationAction={executeNavigationAction}
          renderSubscriptionAction={renderSubscriptionAction}
          utilityItems={sidebar.utilityItems}
          viewerMode={viewerMode}
        />
      </div>

      <div className={styles.sidebarToolsGroup}>
        <CmsWorkspaceSidebarTools
          currentPath={resolvedCurrentPath}
          expanded={isExpanded}
          locale={locale}
          viewerMode={viewerMode}
        />
      </div>

      {expandableEntries.map(({ item, key }) => (
        <WorkspaceMobileDrawer
          id={`workspace-sidebar-mobile-menu-${key}`}
          key={`mobile-menu:${key}`}
          open={isMobileViewport && openExpandableKey === key}
          onOpenChange={(open) => !open && closeMenu()}
          title={item.label}
        >
          <SidebarExpandableMenuContent
            item={item}
            currentPath={resolvedCurrentPath}
            locale={locale}
            onAction={executeNavigationAction}
            onNavigate={closeMenu}
            variant="mobile"
          />
        </WorkspaceMobileDrawer>
      ))}

      {expandableEntries.map(({ item, key }) => {
        const isOpen =
          !isMobileViewport &&
          openExpandableKey === key &&
          menuPosition !== null;

        return (
          <div
            className={styles.menuWrap}
            hidden={!isOpen}
            key={`desktop-menu:${key}`}
            style={
              isOpen
                ? {
                    top: `${menuPosition.top}px`,
                    left: `${menuPosition.left}px`,
                  }
                : undefined
            }
            onMouseEnter={() => openMenuFor(key)}
          >
            <div className={styles.menuPanel} role="menu">
              <SidebarExpandableMenuContent
                item={item}
                currentPath={resolvedCurrentPath}
                locale={locale}
                onAction={executeNavigationAction}
                onNavigate={closeMenu}
              />
            </div>
          </div>
        );
      })}
    </aside>
  );
}
